import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Stack,
  Alert,
  Autocomplete,
  CircularProgress,
  Divider,
  Paper,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { NotificationTemplate } from '../../../types/notification-admin';
import { User } from '../../../types';

interface TemplateTestDialogProps {
  open: boolean;
  template: NotificationTemplate | null;
  users: User[];
  loadingUsers?: boolean;
  onClose: () => void;
  onTestSend: (templateId: string, recipientUserId: string, testData: Record<string, string>) => Promise<void>;
}

/**
 * TemplateTestDialog Component
 * Dialog for testing notification templates by sending test notifications to users
 */
export const TemplateTestDialog: React.FC<TemplateTestDialogProps> = ({
  open,
  template,
  users,
  loadingUsers = false,
  onClose,
  onTestSend,
}) => {
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [variableValues, setVariableValues] = useState<Record<string, string>>({});
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewMessage, setPreviewMessage] = useState('');

  /**
   * Initialize variable values when template changes
   */
  useEffect(() => {
    if (template) {
      const initialValues: Record<string, string> = {};
      template.variables.forEach((variable) => {
        initialValues[variable] = getSampleValue(variable);
      });
      setVariableValues(initialValues);
      setSelectedUser(null);
      setError(null);
      setSuccess(false);
      setShowPreview(false);
    }
  }, [template, open]);

  /**
   * Update preview when variable values change
   */
  useEffect(() => {
    if (template) {
      let title = template.title;
      let message = template.message;

      // Replace all variables with their values
      Object.entries(variableValues).forEach(([key, value]) => {
        const placeholder = `{{${key}}}`;
        const regex = new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
        title = title.replace(regex, value || `{{${key}}}`);
        message = message.replace(regex, value || `{{${key}}}`);
      });

      setPreviewTitle(title);
      setPreviewMessage(message);
    }
  }, [template, variableValues]);

  /**
   * Get sample value for a variable based on its name
   */
  const getSampleValue = (variable: string): string => {
    const lower = variable.toLowerCase();

    // User-related
    if (lower.includes('username') || lower.includes('user_name')) {
      return 'John Smith';
    }
    if (lower.includes('useremail') || lower.includes('user_email')) {
      return 'john.smith@example.com';
    }

    // Person-related
    if (lower.includes('personname') || lower.includes('person_name')) {
      return 'Jane Doe';
    }

    // Content-related
    if (lower.includes('itemname') || lower.includes('item_name')) {
      return 'Family Photo Album';
    }
    if (lower.includes('contenttype') || lower.includes('content_type')) {
      return 'photo';
    }
    if (lower.includes('documentname') || lower.includes('document_name')) {
      return 'Birth Certificate 1920.pdf';
    }

    // Relationship-related
    if (lower.includes('relationship')) {
      return 'Father of Sarah Johnson';
    }
    if (lower.includes('newrole') || lower.includes('new_role')) {
      return 'Editor';
    }

    // Date/Time-related
    if (lower.includes('daysaway') || lower.includes('days_away')) {
      return 'in 3 days';
    }
    if (lower.includes('duedate') || lower.includes('due_date')) {
      return 'December 25, 2024';
    }
    if (lower.includes('maintenancetime') || lower.includes('maintenance_time')) {
      return 'Sunday, 2:00 AM - 4:00 AM EST';
    }

    // Counts
    if (lower.includes('photocount') || lower.includes('photo_count')) {
      return '5';
    }
    if (lower.includes('percentcomplete') || lower.includes('percent_complete')) {
      return '75';
    }

    // Messages and text
    if (lower.includes('reason')) {
      return 'Contains inappropriate content';
    }
    if (lower.includes('topic')) {
      return 'German ancestry records';
    }
    if (lower.includes('taskname') || lower.includes('task_name')) {
      return 'Review family tree edits';
    }
    if (lower.includes('projectname') || lower.includes('project_name')) {
      return 'Smith Family History Project';
    }
    if (lower.includes('eventname') || lower.includes('event_name')) {
      return 'Wedding';
    }

    // Broadcast-related
    if (lower.includes('broadcasttitle') || lower.includes('broadcast_title')) {
      return 'Important Announcement';
    }
    if (lower.includes('broadcastmessage') || lower.includes('broadcast_message')) {
      return 'Please review the updated community guidelines.';
    }
    if (lower.includes('updatemessage') || lower.includes('update_message')) {
      return 'New features have been added to improve your experience.';
    }

    // Feature-related
    if (lower.includes('featurename') || lower.includes('feature_name')) {
      return 'Advanced Search';
    }
    if (lower.includes('featuredescription') || lower.includes('feature_description')) {
      return 'Find ancestors faster with our new advanced search filters.';
    }

    // Default
    return `[${variable}]`;
  };

  /**
   * Validate form data
   */
  const validate = (): boolean => {
    if (!selectedUser) {
      setError('Please select a recipient user');
      return false;
    }

    // Check if all variables have values
    const emptyVars = template?.variables.filter((v) => !variableValues[v]?.trim());
    if (emptyVars && emptyVars.length > 0) {
      setError(`Please provide values for: ${emptyVars.join(', ')}`);
      return false;
    }

    return true;
  };

  /**
   * Handle test send
   */
  const handleTestSend = async () => {
    if (!template || !validate()) {
      return;
    }

    setSending(true);
    setError(null);
    setSuccess(false);

    try {
      await onTestSend(template.id, selectedUser!.id, variableValues);
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err: any) {
      console.error('Error sending test notification:', err);
      setError(err.message || 'Failed to send test notification');
    } finally {
      setSending(false);
    }
  };

  /**
   * Get user display name
   */
  const getUserDisplayName = (user: User): string => {
    return `${user.displayName} (${user.email})`;
  };

  if (!template) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SendIcon />
            <Typography variant="h6">Test Send Template</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Info Alert */}
          <Alert severity="info">
            <Typography variant="body2">
              Send a test notification to verify your template. The notification will be marked as
              [TEST] and will appear in the recipient's notification center.
            </Typography>
          </Alert>

          {/* Success Alert */}
          {success && (
            <Alert severity="success">
              <Typography variant="body2">
                Test notification sent successfully! The dialog will close automatically.
              </Typography>
            </Alert>
          )}

          {/* Error Alert */}
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Template Info */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Template
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {template.title}
            </Typography>
          </Box>

          <Divider />

          {/* Recipient Selection */}
          <Autocomplete
            value={selectedUser}
            onChange={(_, newValue) => setSelectedUser(newValue)}
            options={users}
            getOptionLabel={getUserDisplayName}
            loading={loadingUsers}
            disabled={sending || success}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Recipient"
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {/* Variable Values */}
          {template.variables.length > 0 && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Variable Values
              </Typography>
              <Stack spacing={2}>
                {template.variables.map((variable) => (
                  <TextField
                    key={variable}
                    label={variable}
                    value={variableValues[variable] || ''}
                    onChange={(e) =>
                      setVariableValues({
                        ...variableValues,
                        [variable]: e.target.value,
                      })
                    }
                    size="small"
                    fullWidth
                    required
                    disabled={sending || success}
                    placeholder={`Enter ${variable}`}
                  />
                ))}
              </Stack>
            </Box>
          )}

          <Divider />

          {/* Preview Toggle */}
          <Box>
            <Button
              startIcon={<VisibilityIcon />}
              onClick={() => setShowPreview(!showPreview)}
              size="small"
            >
              {showPreview ? 'Hide' : 'Show'} Preview
            </Button>
          </Box>

          {/* Preview */}
          {showPreview && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: 'background.default',
                border: 1,
                borderColor: 'divider',
              }}
            >
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                Preview (how it will appear to the recipient)
              </Typography>
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  boxShadow: 1,
                  mt: 1,
                }}
              >
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  [TEST] {previewTitle}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ whiteSpace: 'pre-wrap' }}
                >
                  {previewMessage}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 2 }}
                >
                  Just now
                </Typography>
              </Box>
            </Paper>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={sending || success}>
          Cancel
        </Button>
        <Button
          onClick={handleTestSend}
          variant="contained"
          disabled={sending || success || !selectedUser}
          startIcon={sending ? <CircularProgress size={16} /> : <SendIcon />}
        >
          {sending ? 'Sending...' : 'Send Test'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
